/**
 * Tela do calendário de escala.
 *
 * Papel: abre com o cabeçalho do módulo — "Residências", com o subtítulo
 * `FAZENDA BOA VISTA | CALENDÁRIO` e o "← Voltar" para `/residencias` — e logo
 * abaixo dele um cartão branco com a `BarraDoMes` no topo, a grade do mês com a
 * legenda à esquerda e, na coluna de 300 px à direita, os funcionários da
 * escala com as folgas do mês, os prestadores esporádicos com as diárias, as
 * férias e as ações. A caixa do dia, a caixa da cópia da semana, a confirmação
 * de remoção e a folha impressa também são montadas aqui.
 *
 * Como usar: quem tem nível `ver` não recebe `onEscolherDia`, e a coluna
 * recebe `podeEditar` falso: lê a escala inteira sem abrir formulário, campo de
 * acrescentar, cópia nem limpeza. A navegação entre meses, a verificação de
 * conflitos e a impressão continuam disponíveis nesse nível — as três são
 * leitura, e travar a navegação deixaria quem só consulta preso no mês corrente.
 *
 * Até 1080 px de largura a coluna lateral passa para baixo da grade, com um
 * filete em cima no lugar do da esquerda (`max-[1081px]:`, que o Tailwind
 * compila como largura menor que 1081). Abaixo disso a grade de sete colunas não
 * teria largura para as etiquetas de nome ao lado de uma coluna de 300 px.
 *
 * A escala da casa é a lista de `useEquipe`, que se edita na própria coluna. O
 * campo "Adicionar nome..." oferece quem trabalha na casa pelo cadastro de
 * pessoal e não foi desligado; quem é desligado no RH continua na escala até
 * alguém tirá-lo pelo "×".
 *
 * A casa mostrada é, entre as de `casasDaFazenda` — as que ficam dentro da
 * Fazenda Boa Vista e a própria fazenda —, a primeira que tenha alguém `ativo`
 * no cadastro de pessoal. Uma casa de fora da fazenda nunca é escolhida, mesmo
 * vindo antes na lista de `useResidencias` com equipe ativa: o calendário
 * mostraria a equipe dela sob o título da fazenda, e todo dia, diária ou férias
 * gravado iria para a casa errada. Sem casa da fazenda com gente, a tela diz
 * que não há casa com pessoas, em vez de abrir uma grade vazia sem explicação.
 *
 * Os três conjuntos — dias, ausências e visitas — são pedidos pelo recorte da
 * grade inteira (`pontasDaGrade`), e não pelo do mês: a escala se monta por
 * semana, e as células dos meses vizinhos mostram o que está gravado nelas. O
 * que a coluna conta como "do mês" — as folgas, os conflitos, as diárias e as
 * férias da lista — é recortado aqui pelas pontas do mês, e passar a ela os
 * conjuntos da grade faria a contagem de folgas incluir dias de outro mês. A
 * tela espera tudo antes de desenhar a grade; a falha dos dias ou da escala
 * troca o cartão pelo aviso de erro, e a das férias, das diárias ou dos
 * prestadores aparece na seção deles na coluna.
 *
 * "🖨 Imprimir / PDF" monta a `FolhaDoCalendario` com uma `key` nova a cada
 * clique, e enquanto ela está montada todo o resto da página leva
 * `print:hidden` — o papel sai só com a folha. A `key` nova é o que faz um
 * segundo clique imprimir de novo mesmo quando o navegador não avisou que a
 * primeira janela de impressão fechou.
 */
import {
  type Ausencia,
  type DiaCalendario,
  type MembroDaEquipe,
  type Prestador,
  type Visita,
  acessoAlcanca,
  cobreODia,
} from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';
import { NOME_DA_FAZENDA, casasDaFazenda, useResidencias } from '@/modules/cadastros-base';
import { useColaboradores } from '@/modules/rh';
import { AcoesDoMes } from '../components/acoes-do-mes';
import { BarraDoMes } from '../components/barra-do-mes';
import { CaixaCopiarSemana } from '../components/caixa-copiar-semana';
import { FolhaDoCalendario } from '../components/folha-do-calendario';
import { FormularioDia } from '../components/formulario-dia';
import { GradeDoMes } from '../components/grade-do-mes';
import { LegendaDaGrade } from '../components/legenda-da-grade';
import { ListaDeFuncionarios } from '../components/lista-de-funcionarios';
import { PainelPeriodos } from '../components/painel-periodos';
import { candidatosAEscala, nomeNaEscala, pendenciasDoMes } from '../escala';
import {
  MESES,
  type MesMostrado,
  diaDaSemana,
  diaPorExtenso,
  pontasDaGrade,
  pontasDoMes,
} from '../grade';
import {
  useAusenciasDoPeriodo,
  useDiasDoPeriodo,
  useEquipe,
  useEscalaPadrao,
  useExcluirAusencia,
  useExcluirVisita,
  useLimparPeriodo,
  usePrestadores,
  useRemoverPrestador,
  useTirarDaEquipe,
  useVisitasDoPeriodo,
} from '../hooks/usar-calendario';
import { usePrimeiroDia } from '../hooks/usar-primeiro-dia';
import { type PessoaDaEscala } from '../types';

/** O que está esperando confirmação de remoção. */
type AguardandoRemocao =
  | { tipo: 'ausencia'; ausencia: Ausencia }
  | { tipo: 'visita'; visita: Visita }
  | { tipo: 'funcionario'; membro: MembroDaEquipe }
  | { tipo: 'prestador'; prestador: Prestador }
  | { tipo: 'mes' };

/**
 * O título e a frase da confirmação de cada remoção.
 *
 * @param alvo - O que espera a confirmação.
 * @param mostrado - O mês da grade, que a limpeza do mês nomeia.
 * @returns O título, que é a pergunta da origem, e a frase de consequência.
 */
function textoDaRemocao(
  alvo: AguardandoRemocao,
  mostrado: MesMostrado,
): { titulo: string; frase: string } {
  switch (alvo.tipo) {
    case 'ausencia':
      return {
        titulo: `Remover as férias de ${nomeNaEscala(alvo.ausencia.colaborador)}?`,
        frase:
          'O período deixa de aparecer nos dias que cobria. A ação não pode ser desfeita.',
      };
    case 'visita':
      return {
        titulo: `Remover a diária de ${alvo.visita.visitante}?`,
        frase:
          'O período deixa de aparecer nos dias que cobria. A ação não pode ser desfeita.',
      };
    case 'funcionario':
      return {
        titulo: `Remover ${nomeNaEscala(alvo.membro.colaborador)} da lista?`,
        frase: 'Os registros de dias não serão apagados.',
      };
    case 'prestador':
      return {
        titulo: `Remover ${alvo.prestador.nome} da lista de visitantes?`,
        frase: 'As diárias já registradas continuam.',
      };
    case 'mes':
      return {
        titulo: `Apagar todos os registros de ${MESES[mostrado.mes - 1] ?? ''}/${String(mostrado.ano)}?`,
        frase: 'Férias e diárias continuam. A ação não pode ser desfeita.',
      };
  }
}

/**
 * Se um período encosta num intervalo de datas.
 *
 * @param periodo - O que tem `inicio` e `fim` em `AAAA-MM-DD`.
 * @param intervalo - As duas pontas, inclusivas.
 * @returns `true` quando algum dia do período cai no intervalo.
 */
function encostaEm(
  periodo: { inicio: string; fim: string },
  intervalo: { de: string; ate: string },
): boolean {
  return periodo.inicio <= intervalo.ate && periodo.fim >= intervalo.de;
}

/** O mês em que a tela abre: o mês corrente de quem está olhando. */
function mesDeHoje(): MesMostrado {
  const agora = new Date();
  return { ano: agora.getFullYear(), mes: agora.getMonth() + 1 };
}

/**
 * Página do calendário.
 *
 * @returns A tela com o cartão da escala, a caixa do dia, a confirmação de
 *   remoção e, durante a impressão, a folha do mês.
 */
export function PaginaCalendario() {
  const [mostrado, setMostrado] = useState<MesMostrado>(mesDeHoje);
  const [primeiroDia, setPrimeiroDia] = usePrimeiroDia();
  const [diaAberto, setDiaAberto] = useState<string | null>(null);
  const [copiando, setCopiando] = useState(false);
  const [aRemover, setARemover] = useState<AguardandoRemocao | null>(null);
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const [impressaoPedidaEm, setImpressaoPedidaEm] = useState<number | null>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'calendario-boa-vista', 'editar');

  const consultaCasas = useResidencias();
  const consultaPessoas = useColaboradores();

  const colaboradores = consultaPessoas.data ?? [];
  const ativos = colaboradores.filter((p) => p.status === 'ativo');
  const casas = casasDaFazenda(consultaCasas.data ?? []);
  const casaComEscala = casas.find((casa) => ativos.some((p) => p.residencia.id === casa.id));
  const residenciaId = casaComEscala?.id ?? '';
  const temCasa = residenciaId !== '';

  const recorte = { residenciaId, ...pontasDaGrade(mostrado, primeiroDia) };
  const doMes = pontasDoMes(mostrado);

  const consultaDias = useDiasDoPeriodo(recorte, temCasa);
  const consultaAusencias = useAusenciasDoPeriodo(recorte, temCasa);
  const consultaVisitas = useVisitasDoPeriodo(recorte, temCasa);
  const consultaEquipe = useEquipe(residenciaId, temCasa);
  const consultaPrestadores = usePrestadores(residenciaId, temCasa);
  const consultaEscalaPadrao = useEscalaPadrao(residenciaId, temCasa);

  const excluirAusencia = useExcluirAusencia();
  const excluirVisita = useExcluirVisita();
  const tirarDaEquipe = useTirarDaEquipe();
  const removerPrestador = useRemoverPrestador();
  const limparPeriodo = useLimparPeriodo();
  const removendo =
    excluirAusencia.isPending ||
    excluirVisita.isPending ||
    tirarDaEquipe.isPending ||
    removerPrestador.isPending ||
    limparPeriodo.isPending;

  const dias = consultaDias.data ?? [];
  const ausencias = consultaAusencias.data ?? [];
  const visitas = consultaVisitas.data ?? [];
  const membros = consultaEquipe.data ?? [];
  const prestadores = consultaPrestadores.data ?? [];

  const diasDoMes = dias.filter((d) => d.dia >= doMes.de && d.dia <= doMes.ate);
  const ausenciasDoMes = ausencias.filter((a) => encostaEm(a, doMes));
  const visitasDoMes = visitas.filter((v) => encostaEm(v, doMes));

  const equipe: PessoaDaEscala[] = membros.map((m) => m.colaborador);
  const idsDaEquipe = equipe.map((p) => p.id);
  const candidatos = candidatosAEscala(colaboradores, residenciaId, idsDaEquipe);
  // Quem saiu da escala continua nos dias em que foi escalado, com o nome
  // gravado ali; sem ele no mapa, a lista de conflitos o escreveria como "—".
  const nomePorId = new Map([
    ...dias.flatMap((d) =>
      d.escalados.map((e) => [e.colaboradorId, nomeNaEscala(e)] as const),
    ),
    ...equipe.map((p) => [p.id, nomeNaEscala(p)] as const),
  ]);

  const carregando =
    consultaCasas.isPending ||
    consultaPessoas.isPending ||
    (temCasa &&
      (consultaDias.isPending ||
        consultaAusencias.isPending ||
        consultaVisitas.isPending ||
        consultaEquipe.isPending ||
        consultaPrestadores.isPending ||
        consultaEscalaPadrao.isPending));
  const erro =
    consultaCasas.error ?? consultaPessoas.error ?? consultaDias.error ?? consultaEquipe.error;

  /** Fecha a caixa do dia, seja pelo botão, pelo `Esc` ou por clique no véu. */
  function handleDiaOpenChange(aberto: boolean) {
    if (!aberto) setDiaAberto(null);
  }

  /** Abre a confirmação de remoção, limpando a falha da tentativa anterior. */
  function handlePedirRemocao(alvo: AguardandoRemocao) {
    setErroDaRemocao(null);
    setARemover(alvo);
  }

  /** Fecha a confirmação de remoção, com a falha anterior junto. */
  function handleRemocaoOpenChange(aberto: boolean) {
    if (aberto) return;
    setARemover(null);
    setErroDaRemocao(null);
  }

  /** Apaga o período confirmado e fecha a caixa, se a API aceitar. */
  async function handleConfirmarRemocao() {
    if (aRemover === null) return;
    setErroDaRemocao(null);

    try {
      if (aRemover.tipo === 'ausencia')
        await excluirAusencia.mutateAsync(aRemover.ausencia.id);
      else if (aRemover.tipo === 'visita') await excluirVisita.mutateAsync(aRemover.visita.id);
      else if (aRemover.tipo === 'funcionario')
        await tirarDaEquipe.mutateAsync(aRemover.membro.id);
      else if (aRemover.tipo === 'prestador')
        await removerPrestador.mutateAsync(aRemover.prestador.id);
      else await limparPeriodo.mutateAsync({ residenciaId, ...doMes });
      setARemover(null);
    } catch (falha) {
      setErroDaRemocao(
        falha instanceof ErroDaApi
          ? falha.message
          : 'A remoção falhou antes de chegar ao servidor.',
      );
    }
  }

  const registroDoDia: DiaCalendario | null =
    diaAberto === null ? null : (dias.find((d) => d.dia === diaAberto) ?? null);

  const textoDaConfirmacao =
    aRemover === null ? { titulo: '', frase: '' } : textoDaRemocao(aRemover, mostrado);
  const apagandoMes = aRemover?.tipo === 'mes';

  return (
    <>
      <div className={cn(impressaoPedidaEm !== null && 'print:hidden')}>
        <CabecalhoDoModulo
          titulo="Residências"
          subtitulo="FAZENDA BOA VISTA | CALENDÁRIO"
          voltarPara="/residencias"
        />

        <div className="mb-9 rounded-md border border-linha-forte bg-superficie p-[20px_22px_24px]">
          <BarraDoMes
            mostrado={mostrado}
            onMudar={setMostrado}
            onHoje={() => setMostrado(mesDeHoje())}
            primeiroDia={primeiroDia}
            onTrocarPrimeiroDia={setPrimeiroDia}
          />

          {erro !== null && erro !== undefined ? (
            <div className="rounded-sm border border-perigo/40 bg-perigo/5 p-6">
              <p className="mb-3 text-sm text-perigo">
                Não foi possível carregar o calendário.
              </p>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void consultaDias.refetch();
                  void consultaEquipe.refetch();
                }}
              >
                Tentar de novo
              </Button>
            </div>
          ) : carregando ? (
            <Skeleton className="h-[640px] w-full" />
          ) : !temCasa ? (
            <p className="py-1 text-sm text-texto-fraco">
              Nenhuma casa da {NOME_DA_FAZENDA} tem pessoas cadastradas. A escala começa pelo
              cadastro do pessoal.
            </p>
          ) : (
            <div className="grid grid-cols-[1fr_300px] items-start gap-6 max-[1081px]:grid-cols-1">
              <div className="min-w-0">
                <GradeDoMes
                  mostrado={mostrado}
                  primeiroDia={primeiroDia}
                  dias={dias}
                  ausencias={ausencias}
                  visitas={visitas}
                  daCasa={idsDaEquipe}
                  hoje={hojeEmIso()}
                  onEscolherDia={podeEditar ? setDiaAberto : undefined}
                />
                <LegendaDaGrade />
              </div>

              <div className="border-l border-linha-forte pl-6 max-[1081px]:mt-1 max-[1081px]:border-t max-[1081px]:border-l-0 max-[1081px]:pt-5 max-[1081px]:pl-0">
                <ListaDeFuncionarios
                  equipe={membros}
                  dias={diasDoMes}
                  residenciaId={residenciaId}
                  candidatos={candidatos}
                  podeEditar={podeEditar}
                  onRemover={(membro) => handlePedirRemocao({ tipo: 'funcionario', membro })}
                />
                <PainelPeriodos
                  ausencias={ausenciasDoMes}
                  visitas={visitasDoMes}
                  prestadores={prestadores}
                  pessoas={equipe}
                  residenciaId={residenciaId}
                  podeEditar={podeEditar}
                  falhouAusencias={consultaAusencias.error !== null}
                  falhouVisitas={consultaVisitas.error !== null}
                  falhouPrestadores={consultaPrestadores.error !== null}
                  onRemoverAusencia={(ausencia) =>
                    handlePedirRemocao({ tipo: 'ausencia', ausencia })
                  }
                  onRemoverVisita={(visita) => handlePedirRemocao({ tipo: 'visita', visita })}
                  onRemoverPrestador={(prestador) =>
                    handlePedirRemocao({ tipo: 'prestador', prestador })
                  }
                />
                <AcoesDoMes
                  pendencias={pendenciasDoMes(diasDoMes, ausencias, idsDaEquipe)}
                  nomePorId={nomePorId}
                  onImprimir={() => setImpressaoPedidaEm(Date.now())}
                  podeEditar={podeEditar}
                  onCopiarSemana={() => setCopiando(true)}
                  onLimparMes={() => handlePedirRemocao({ tipo: 'mes' })}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={diaAberto !== null} onOpenChange={handleDiaOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {diaAberto === null ? '' : `Editar ${diaPorExtenso(diaAberto)}`}
            </DialogTitle>
          </DialogHeader>
          {diaAberto !== null && (
            <FormularioDia
              key={diaAberto}
              dia={diaAberto}
              residenciaId={residenciaId}
              registro={registroDoDia}
              pessoas={equipe}
              ausencias={ausencias.filter((a) => cobreODia(a, diaAberto))}
              escalaPadrao={(consultaEscalaPadrao.data ?? []).find(
                (padrao) => padrao.diaDaSemana === diaDaSemana(diaAberto),
              )}
              onSalvo={() => setDiaAberto(null)}
              onCancelar={() => setDiaAberto(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={copiando} onOpenChange={setCopiando}>
        <DialogContent className="w-[440px]">
          <DialogHeader>
            <DialogTitle>Copiar semana anterior</DialogTitle>
          </DialogHeader>
          {copiando && (
            <CaixaCopiarSemana
              mostrado={mostrado}
              primeiroDia={primeiroDia}
              residenciaId={residenciaId}
              onConcluida={() => setCopiando(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={aRemover !== null} onOpenChange={handleRemocaoOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{textoDaConfirmacao.titulo}</AlertDialogTitle>
            <AlertDialogDescription>{textoDaConfirmacao.frase}</AlertDialogDescription>
          </AlertDialogHeader>

          {erroDaRemocao !== null && <p className="text-sm text-perigo">{erroDaRemocao}</p>}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={removendo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(evento) => {
                evento.preventDefault();
                void handleConfirmarRemocao();
              }}
              disabled={removendo}
            >
              {apagandoMes
                ? removendo
                  ? 'Apagando…'
                  : 'Apagar'
                : removendo
                  ? 'Removendo…'
                  : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {impressaoPedidaEm !== null && (
        <FolhaDoCalendario
          key={impressaoPedidaEm}
          mostrado={mostrado}
          primeiroDia={primeiroDia}
          dias={dias}
          ausencias={ausencias}
          visitas={visitas}
          onConcluida={() => setImpressaoPedidaEm(null)}
        />
      )}
    </>
  );
}
